import SearchIcon from '@mui/icons-material/Search';
import { Box, Grid, InputBase, Stack } from '@mui/material';
import { debounce, isEmpty } from 'lodash-es';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import {
  ClientContactShareMultipleDTO,
  IRecruiterSearchDTO,
  assignClientUser,
  assignClientContact,
  IWorkspace,
  getWorkspaceUsers,
  usersAdvanceFilterQuery,
} from 'wisestep-api';
import { WsCheckboxList, WsButton } from 'wisestep-base';

import WsCheckBox from '../../../../components/WsCheckBox/WsCheckBox';
import { warning, success } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';

import { StyledFooter } from './types';

export interface IRecruitersListProps {
  clientId?: string;
  clientIds?: string[];
  clientContactIds?: string[];
  onAssign(selectedList?: string[], idNameMapping?: { email?: string; name?: string }): void;
  workspaceRecruiters?: IRecruiterSearchDTO[];
  assignedUsers?: string[];
  moduleName: string;
}

const ClientUserList = ({
  clientId,
  clientIds,
  clientContactIds,
  onAssign,
  assignedUsers,
  moduleName,
}: IRecruitersListProps) => {
  const setNotification = useNotificationStore((state) => state.setNotification);
  const methods = useForm();
  const { watch, setValue } = methods;
  const [usersList, setUsersList] = useState<IWorkspace[]>();
  const abortControllerRef = useRef(new AbortController());
  const usersListRef = useRef<IWorkspace[] | null>(null);

  const recruitersOptions: {
    [key: string]: string;
  } = {};

  const getRecruiterFullName = (recruiter: IWorkspace) => {
    if (recruiter.userName || recruiter.userId) {
      return `${recruiter.userName ?? ''} (${recruiter.userId ?? ''})`;
    }
    return '';
  };

  usersList?.forEach((recruiter) => {
    if (recruiter.userId && !assignedUsers?.some((email) => email === recruiter.userId)) {
      recruitersOptions[recruiter.userId] = getRecruiterFullName(recruiter);
    }
  });

  const getWorkspaceUsersQuery = async () => {
    const query = {
      roles: [],
      statuses: ['activated'],
      userIds: [],
      page: 0,
      size: 20,
    };
    const response = await getWorkspaceUsers(query);
    const workspaceUsers: IWorkspace[] = response.data.workspaceUsers;
    return workspaceUsers;
  };

  const getUserList = useCallback(async () => {
    const assignedToData = await getWorkspaceUsersQuery();
    usersListRef.current = assignedToData;
    setUsersList(assignedToData);
  }, []);

  useEffect(() => {
    getUserList();
  }, [getUserList]);

  const assignClient = async () => {
    try {
      let response;
      if (moduleName == 'CLIENT') {
        const clientShareDTO = { clientIds: clientIds as [], userList: watch('recruitersList') };
        response = await assignClientUser(clientShareDTO);
      } else {
        const clientShareDTO: ClientContactShareMultipleDTO = {
          clientId: clientId as string,
          clientContactIds: clientContactIds as [],
          userList: watch('recruitersList'),
        };
        response = await assignClientContact(clientShareDTO);
      }
      if (response.status === 200) {
        setNotification(success('User assigned Successfully'));
      }
      onAssign(JSON.parse(response?.config?.data)?.userList, recruitersOptions);
    } catch (error) {
      setNotification(warning('Something Went Wrong , Please try again'));
    }
  };

  const handleSelectEvents = (checked: boolean) =>
    checked
      ? setValue('recruitersList', Object.keys(recruitersOptions))
      : setValue('recruitersList', []);

  const onUserInputKey = async (searchText: string) => {
    if (searchText?.length > 2) {
      if (abortControllerRef.current) abortControllerRef.current.abort();
      const newAbortController = new AbortController();
      abortControllerRef.current = newAbortController;
      try {
        const response = await usersAdvanceFilterQuery(
          `${encodeURIComponent(
            searchText
              ?.trim()
              .split(' ')
              .map((name) => (isEmpty(name) ? '' : name + ':*'))
              .join(' & ')
          )}`
        );
        const workspaceUsers = response.data;
        const extractedUserData = workspaceUsers
          .filter((user) => user.accepted === true && user.barred === false)
          .map((user) => ({
            userId: user.userId,
            userName: user.userName,
          }));
        setUsersList(extractedUserData);
      } catch (error) {
        console.error('Error fetching user:', error);
      }
    }
  };
  const debouncedUsersInput = (event) => {
    isEmpty(event.target.value)
      ? usersListRef.current &&
        usersListRef.current.length > 0 &&
        setUsersList(usersListRef.current)
      : debounce(onUserInputKey, 150)(event.target.value);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <FormProvider {...methods}>
        <Stack direction="row" spacing={1} alignItems={'center'}>
          <SearchIcon />
          <InputBase
            placeholder="Search User"
            name="recruitersFilter"
            onChange={debouncedUsersInput}
            fullWidth
          />
        </Stack>
        <Stack spacing={1} sx={{ height: 'calc(100vh - 240px)', overflowY: 'auto' }}>
          <WsCheckBox
            name="selectAll"
            label="Select All Users"
            bold={true}
            onClick={handleSelectEvents}
          />
          <WsCheckboxList name="recruitersList" options={recruitersOptions} />
        </Stack>
        <StyledFooter>
          <Grid container item justifyContent="space-between" py={1}>
            <WsButton variant="text" size="large">
              {`${
                (methods.watch('recruitersList') && methods.watch('recruitersList').length) ?? 0
              } Selected`}
            </WsButton>
            <WsButton variant="contained" size="large" onClick={assignClient}>
              Assign
            </WsButton>
          </Grid>
        </StyledFooter>
      </FormProvider>
    </Box>
  );
};

export default ClientUserList;
